import { cn } from '@/lib/utils';
import { useMemo, useRef, type KeyboardEvent, type PointerEvent } from 'react';
import { displayColour } from './appearance-colour.util';
import {
  fieldColorAt,
  handleKeyColor,
  MAX_HUE,
  SLIDER_KEYS,
} from './appearance-controls.util';
import { normalizeColor, type AppearanceColor } from './appearance.util';

/** The field's hue stops, every 30 degrees round the wheel. */
const HUE_STOPS = Array.from({ length: 13 }, (_, index) => index * 30);

/**
 * How far the handles' box sits inside the field, in pixels: at least the
 * chosen handle's radius (`size-7`), so it stays whole at an edge.
 */
const FIELD_INSET = 16;

/** A point `share` of the way across the handles' box, as a CSS length. */
const across = (share: number) =>
  `calc(${FIELD_INSET}px + (100% - ${FIELD_INSET * 2}px) * ${share.toFixed(4)})`;

/**
 * A horizontal run through the hues at one tone, as CSS, laid over the
 * handles' box so each handle sits on its own hue and tone.
 */
function hueRow(tone: number): string {
  const stops = HUE_STOPS.map((hue) => {
    const share = Math.min(1, hue / MAX_HUE);
    return `${displayColour(hue % 360, tone, 100)} ${across(share)}`;
  });
  return `linear-gradient(to right, ${stops.join(', ')})`;
}

/** From the top row's tone at the top of the handles' box to none at its foot. */
const TONE_FADE = `linear-gradient(to bottom, #000 ${across(0)}, transparent ${across(1)})`;

const DOTS =
  'radial-gradient(circle at center, #000 1.6px, transparent 2.1px) 0 0 / 10px 10px';

const replaceAt = (
  colors: AppearanceColor[],
  index: number,
  color: AppearanceColor
) =>
  index >= colors.length
    ? [...colors, color]
    : colors.map((entry, at) => (at === index ? color : entry));

interface Drag {
  pointerId: number;
  index: number;
  start: AppearanceColor[];
}

/** Keys that remove the colour whose handle has focus. */
const REMOVE_KEYS = new Set(['Delete', 'Backspace']);

/**
 * The dotted field: hue left to right, tone light at the top to dark at the
 * bottom, with a round handle for each colour, drawn as the field draws that
 * point. Drag a handle, or press the field to bring the chosen handle there;
 * moves preview, the release saves, Escape puts the handle back. Each handle
 * is a slider for the keyboard, and Delete or Backspace removes its colour
 * while there is more than one.
 */
export function ColourField({
  colors,
  intensity,
  active,
  onActiveChange,
  onChange,
  onCommit,
  onCancel,
  onRemove,
  handleRef,
}: {
  colors: AppearanceColor[];
  intensity: number;
  active: number;
  onActiveChange: (index: number) => void;
  /** A move to preview. */
  onChange: (colors: AppearanceColor[]) => void;
  /** Saves the last move. */
  onCommit: () => void;
  /** Drops the moves since the last save. */
  onCancel: () => void;
  /** Removes a colour. */
  onRemove: (index: number) => void;
  handleRef: (index: number, node: HTMLDivElement | null) => void;
}) {
  const areaRef = useRef<HTMLDivElement>(null);
  const drag = useRef<Drag | null>(null);
  // A key held on a handle: its release saves.
  const keyMoved = useRef(false);
  const top = useMemo(() => hueRow(100), []);
  const bottom = useMemo(() => hueRow(0), []);

  const pointColor = (event: PointerEvent) => {
    const rect = areaRef.current?.getBoundingClientRect();
    return rect
      ? fieldColorAt(rect, event.clientX, event.clientY)
      : { hue: 0, tone: 50 };
  };

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 || drag.current) return;
    const handle = (event.target as HTMLElement).closest<HTMLElement>(
      '[data-handle]'
    );
    const onHandle = handle !== null;
    const index = onHandle
      ? Number(handle.dataset.handle)
      : Math.min(active, colors.length);
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    drag.current = { pointerId: event.pointerId, index, start: colors };
    onActiveChange(index);
    // The handle keeps its place until it moves; the field brings it here.
    if (!onHandle) onChange(replaceAt(colors, index, pointColor(event)));
    // Pressing stops the browser moving focus, so Escape reaches the handle.
    const target =
      handle ??
      areaRef.current?.querySelector<HTMLElement>(`[data-handle="${index}"]`);
    target?.focus();
  };

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const current = drag.current;
    if (!current || current.pointerId !== event.pointerId) return;
    onChange(replaceAt(current.start, current.index, pointColor(event)));
  };

  const endDrag = (event: PointerEvent<HTMLDivElement>, save: boolean) => {
    const current = drag.current;
    if (!current || current.pointerId !== event.pointerId) return;
    drag.current = null;
    if (event.currentTarget.hasPointerCapture?.(event.pointerId))
      event.currentTarget.releasePointerCapture(event.pointerId);
    if (save) onCommit();
    else onCancel();
  };

  const onHandleKeyDown = (
    event: KeyboardEvent<HTMLDivElement>,
    index: number
  ) => {
    if (event.key === 'Escape') {
      if (!drag.current && !keyMoved.current) return;
      event.preventDefault();
      event.stopPropagation();
      drag.current = null;
      keyMoved.current = false;
      onCancel();
      return;
    }
    if (REMOVE_KEYS.has(event.key)) {
      if (colors.length <= 1 || drag.current) return;
      event.preventDefault();
      keyMoved.current = false;
      onRemove(index);
      return;
    }
    const next = handleKeyColor(colors[index], event.key, event.shiftKey);
    if (!next) return;
    event.preventDefault();
    keyMoved.current = true;
    onChange(replaceAt(colors, index, normalizeColor(next)));
  };

  const onHandleKeyUp = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!SLIDER_KEYS.has(event.key) || !keyMoved.current) return;
    keyMoved.current = false;
    onCommit();
  };

  // The field clips only its dots, so a handle at an edge keeps its focus
  // ring.
  return (
    <div
      role='group'
      aria-label='Colours'
      className='relative aspect-[4/3] w-full cursor-crosshair touch-none rounded-xl bg-muted/50 select-none'
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={(event) => endDrag(event, true)}
      onPointerCancel={(event) => endDrag(event, false)}
    >
      <div
        aria-hidden
        className='absolute inset-0 overflow-hidden rounded-xl'
        style={{ mask: DOTS, WebkitMask: DOTS }}
      >
        <div className='absolute inset-0' style={{ background: bottom }} />
        <div
          className='absolute inset-0'
          style={{
            background: top,
            maskImage: TONE_FADE,
            WebkitMaskImage: TONE_FADE,
          }}
        />
      </div>
      <div
        ref={areaRef}
        className='absolute'
        style={{
          top: FIELD_INSET,
          right: FIELD_INSET,
          bottom: FIELD_INSET,
          left: FIELD_INSET,
        }}
      >
        {colors.map((color, index) => {
          const selected = index === active;
          return (
            <div
              key={index}
              ref={(node) => handleRef(index, node)}
              role='slider'
              tabIndex={0}
              data-handle={index}
              data-active={selected || undefined}
              aria-label={`Colour ${index + 1}`}
              aria-valuemin={0}
              aria-valuemax={MAX_HUE}
              aria-valuenow={color.hue}
              aria-valuetext={`Hue ${color.hue}, tone ${color.tone}`}
              aria-keyshortcuts={
                colors.length > 1 ? 'Delete Backspace' : undefined
              }
              onFocus={() => onActiveChange(index)}
              onKeyDown={(event) => onHandleKeyDown(event, index)}
              onKeyUp={onHandleKeyUp}
              className={cn(
                'absolute -translate-x-1/2 -translate-y-1/2 cursor-grab rounded-full border-2 border-white',
                'shadow-[0_1px_3px_rgba(0,0,0,0.3),0_0_0_0.5px_rgba(0,0,0,0.2)]',
                'ring-offset-background focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-hidden',
                selected ? 'z-10 size-7' : 'size-5'
              )}
              style={{
                left: `${(color.hue / MAX_HUE) * 100}%`,
                top: `${100 - color.tone}%`,
                backgroundColor: displayColour(
                  color.hue,
                  color.tone,
                  intensity
                ),
              }}
            />
          );
        })}
      </div>
    </div>
  );
}
